<template>
  <Teleport to="body">
    <div
      v-if="modelValue"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div class="absolute inset-0 bg-black/50" @click="close" />
      <div class="relative bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
        <!-- Header -->
        <div class="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100 shrink-0">
          <h2 class="text-lg font-semibold text-gray-900">
            {{ selectedAssignment ? 'Add a Message' : 'Post Assignment' }}
          </h2>
          <button
            class="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            @click="close"
          >
            <font-awesome-icon :icon="['fas', 'xmark']" />
          </button>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-y-auto px-5 py-4">
          <!-- Step 2: Message input -->
          <template v-if="selectedAssignment">
            <!-- Selected assignment (click to go back) -->
            <button
              class="w-full flex items-center gap-3 p-3 rounded-lg bg-cyan-50 border border-cyan-200 text-left hover:bg-cyan-100 transition-colors mb-4"
              @click="selectedAssignment = null"
            >
              <div class="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center shrink-0">
                <font-awesome-icon :icon="['fas', 'file-lines']" class="text-cyan-600 text-sm" />
              </div>
              <div class="min-w-0 flex-1">
                <p class="text-sm font-medium text-gray-900 truncate">{{ selectedAssignment.assessmentName }}</p>
                <p v-if="selectedAssignment.dateDue" class="text-xs text-gray-500">
                  Due: {{ formatDate(selectedAssignment.dateDue) }}
                </p>
              </div>
              <font-awesome-icon :icon="['fas', 'chevron-left']" class="text-xs text-gray-400" />
            </button>

            <textarea
              v-model="message"
              class="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-400 resize-none"
              rows="3"
              placeholder="Add a message for your students (optional)"
            />
          </template>

          <!-- Step 1: Pick an assignment -->
          <template v-else>
            <!-- Loading -->
            <div v-if="loading" class="flex justify-center py-8">
              <div class="w-6 h-6 border-2 border-gray-300 border-t-cyan-600 rounded-full animate-spin" />
            </div>

            <!-- Empty -->
            <div v-else-if="!assignments.length" class="py-8 text-center">
              <div class="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mx-auto mb-3">
                <font-awesome-icon :icon="['fas', 'file-lines']" class="text-gray-400 text-lg" />
              </div>
              <p class="text-sm font-medium text-gray-700 mb-1">No assignments to post</p>
              <p class="text-xs text-gray-500">
                Create assignments in the gradebook first, or all assignments have already been posted.
              </p>
            </div>

            <!-- Assignment list -->
            <template v-else>
              <p class="text-sm text-gray-500 mb-3">Choose an assignment to share with your class</p>
              <div class="space-y-1">
                <button
                  v-for="a in assignments"
                  :key="a.id"
                  class="w-full flex items-center gap-3 p-3 rounded-lg text-left hover:bg-gray-50 transition-colors"
                  @click="selectedAssignment = a"
                >
                  <div class="w-10 h-10 rounded-lg bg-cyan-50 flex items-center justify-center shrink-0">
                    <font-awesome-icon :icon="['fas', 'file-lines']" class="text-cyan-600 text-sm" />
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-sm font-medium text-gray-900 truncate">{{ a.assessmentName }}</p>
                    <p v-if="a.dateDue" class="text-xs text-gray-500">
                      Due: {{ formatDate(a.dateDue) }}
                    </p>
                  </div>
                  <font-awesome-icon :icon="['fas', 'chevron-right']" class="text-xs text-gray-400" />
                </button>
              </div>
            </template>
          </template>
        </div>

        <!-- Footer (step 2 only) -->
        <div v-if="selectedAssignment" class="px-5 pb-5 pt-3 border-t border-gray-100 shrink-0">
          <AppButton
            variant="primary"
            size="md"
            :full-width="true"
            :loading="posting"
            @click="handlePost"
          >
            <font-awesome-icon :icon="['fas', 'paper-plane']" class="text-xs" />
            Post to Space
          </AppButton>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { AppButton } from '@/design-system/components'
import { connectApi, gradebookApi } from '@/api'
import type { SpacePost, GradebookAssignment } from '@/api'

const props = defineProps<{
  modelValue: boolean
  spaceId: string
  classId: string
  existingPosts: SpacePost[]
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  posted: []
}>()

const assignments = ref<GradebookAssignment[]>([])
const loading = ref(false)
const selectedAssignment = ref<GradebookAssignment | null>(null)
const message = ref('')
const posting = ref(false)

watch(() => props.modelValue, async (open) => {
  if (open) {
    selectedAssignment.value = null
    message.value = ''
    await loadAssignments()
  }
})

async function loadAssignments() {
  if (!props.classId) return
  loading.value = true
  try {
    const gradebook = await gradebookApi.getGradebook(props.classId)
    const postedAssignmentIds = new Set(
      props.existingPosts
        .filter(p => p.type === 'ASSIGNMENT' && p.assignment)
        .map(p => p.assignment!.id)
    )
    assignments.value = gradebook.assignments.filter(a => !postedAssignmentIds.has(a.id))
  } catch (err) {
    console.error('Failed to load assignments:', err)
    assignments.value = []
  } finally {
    loading.value = false
  }
}

async function handlePost() {
  if (!selectedAssignment.value) return
  posting.value = true
  try {
    await connectApi.createPost(props.spaceId, {
      type: 'ASSIGNMENT',
      body: message.value.trim() || '',
      assignmentId: selectedAssignment.value.id,
    })
    emit('posted')
    close()
  } catch (err) {
    console.error('Failed to post assignment:', err)
  } finally {
    posting.value = false
  }
}

function close() {
  emit('update:modelValue', false)
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr)
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
  } catch {
    return dateStr
  }
}
</script>
